import React, { useEffect, useState } from 'react'
import {
  CBadge,
  CButton,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
  CSmartTable,
} from '@coreui/react-pro'
import moment from 'moment'

import ToastComponent from 'src/components/common/TaostComponent.js'

const columns = [
  { label: 'FID', key: 'id', _style: { width: '10%' } },
  { label: 'Fixture', key: 'fixture_name' },
  { label: 'Start', key: 'start_date', _style: { width: '20%' } },
  { label: 'End', key: 'end_date', _style: { width: '20%' } },
  { label: 'Status', key: 'status', _style: { width: '15%' } },
]

// A date with its time underneath, matching how the Fixtures listing prints them.
const DateCell = ({ date, time }) => {
  if (!date) return <>—</>
  return (
    <>
      <div>{moment(date).format('D.MM.YYYY')}</div>
      {time && (
        <div className="text-medium-emphasis small">
          {moment(time, ['HH:mm:ss', 'HH:mm']).format('h:mm A')}
        </div>
      )}
    </>
  )
}

// Read-only fixtures list opened from the "Fixtures" count on the teams and rounds listings.
// `loadFixtures` fetches the rows (team schedule or round fixtures); `sourceId` refetches
// when a different row is opened. Same table styling as the Players listing.
const FixturesModal = ({
  title,
  sourceId,
  loadFixtures,
  visible,
  onClose,
  emptyLabel = 'No fixtures yet.',
}) => {
  const [fixtures, setFixtures] = useState([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!visible || !sourceId) return undefined

    let cancelled = false
    setFixtures([])
    setLoading(true)
    loadFixtures(sourceId)
      .then((res) => {
        if (cancelled) return
        if (res.status === 200) {
          setFixtures(res.data || [])
        } else {
          ToastComponent(res.message || 'Unable to load fixtures.', 'error')
        }
      })
      .catch(() => {
        if (!cancelled) ToastComponent('Unable to load fixtures.', 'error')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, sourceId])

  return (
    <CModal visible={visible} onClose={onClose} size="xl" alignment="center">
      <CModalHeader>
        <CModalTitle>{title}</CModalTitle>
      </CModalHeader>
      <CModalBody>
        <div className="ge-datatable ge-datatable--static">
          <CSmartTable
            columns={columns}
            items={fixtures}
            itemsPerPage={10}
            loading={loading}
            pagination={fixtures.length > 10}
            noItemsLabel={emptyLabel}
            scopedColumns={{
              fixture_name: (item) => (
                <td>
                  <div className="fw-semibold">
                    {item.team2_name ? `${item.team_name} vs ${item.team2_name}` : item.team_name}
                  </div>
                  <div className="text-medium-emphasis small">
                    {[item.round ? `Round ${item.round}` : null, item.match_type]
                      .filter(Boolean)
                      .join(' • ')}
                  </div>
                </td>
              ),
              start_date: (item) => (
                <td>
                  <DateCell date={item.start_date} time={item.start_time} />
                </td>
              ),
              end_date: (item) => (
                <td>
                  <DateCell date={item.end_date} time={item.end_time} />
                </td>
              ),
              status: (item) => (
                <td>
                  {item.status === 0 && <CBadge color="warning">Not Started</CBadge>}
                  {item.status === 1 && <CBadge color="danger">In-Progress</CBadge>}
                  {item.status === 2 && <CBadge color="success">Completed</CBadge>}
                </td>
              ),
            }}
            tableProps={{ hover: true, responsive: true, className: 'align-middle mb-0' }}
          />
        </div>
      </CModalBody>
      <CModalFooter>
        <CButton color="secondary" onClick={onClose}>
          Close
        </CButton>
      </CModalFooter>
    </CModal>
  )
}

export default FixturesModal
